'use client'
import { useTranslate } from '@repo/shared/hooks'
import { normalizeDigits } from '@repo/ui/lib'
import { OTP_LENGTH } from '@repo/shared/variables/otp.var'
import { Button, Col, Icon, Input, Row, Typography } from '@repo/ui/components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { OtpResend } from './otp.resend'
import type { IOtpProps } from './otp.types'
import { twMerge } from 'tailwind-merge'
import { useWebOtp } from '@repo/shared/hooks'

export const Otp = (props: IOtpProps) => {
  const {
    mobileOrEmail,
    onResend,
    onComplete,
    onEdit,
    focusRef,
    resendLoading = false,
    resendError = false,
  } = props
  const { tRich } = useTranslate()
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  if (inputRefs.current.length === 0) {
    inputRefs.current = Array(OTP_LENGTH).fill(null)
  }

  // WebOTP integration for automatic OTP detection
  const handleWebOtpReceived = useCallback((receivedOtp: string) => {
    const normalizedOtp = normalizeDigits(receivedOtp)
    if (normalizedOtp.length === OTP_LENGTH && /^\d+$/.test(normalizedOtp)) {
      const otpArray = normalizedOtp.split('')
      setOtp(otpArray)
      setTimeout(() => {
        if (onComplete) {
          onComplete(normalizedOtp)
        }
      }, 100)
    }
  }, [])

  const { isSupported } = useWebOtp({
    onOtpReceived: handleWebOtpReceived,
    enabled: true,
  })

  const setRef = useCallback(
    (index: number) => (el: HTMLInputElement | null) => {
      inputRefs.current[index] = el
    },
    [],
  )

  const handleChange = (index: number, value: string) => {
    // Normalize digits first to handle Persian numbers
    const normalizedValue = normalizeDigits(value)

    // Check if the normalized value contains only digits
    if (!/^\d*$/.test(normalizedValue)) return

    if (normalizedValue.length > 1) {
      const digits = normalizedValue.split('').slice(0, OTP_LENGTH)
      const newOtp = [...otp]
      digits.forEach((digit, i) => {
        if (index + i < OTP_LENGTH) {
          newOtp[index + i] = digit
        }
      })
      setOtp(newOtp)

      const nextEmptyIndex = newOtp.findIndex((digit) => !digit)
      const focusIndex = nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex
      inputRefs.current[focusIndex]?.focus()
      return
    }

    const newOtp = [...otp]
    newOtp[index] = normalizedValue
    setOtp(newOtp)

    if (normalizedValue && index < OTP_LENGTH - 1) {
      const nextInput = inputRefs.current[index + 1]
      if (nextInput) {
        nextInput.focus()
      }
    } else if (normalizedValue && index === OTP_LENGTH - 1) {
      if (focusRef?.current) {
        setTimeout(() => {
          focusRef.current?.click()
        }, 200)
      }
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    // Only handle backspace for navigation
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1]
      if (prevInput) {
        prevInput.focus()
      }
    }
  }

  const handleFocus = (index: number) => {
    inputRefs.current[index]?.select()
  }

  const handleResend = () => {
    setOtp(Array(OTP_LENGTH).fill(''))

    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }

    onResend()
  }

  const handlePaste = (e: React.ClipboardEvent, index: number) => {
    e.preventDefault()
    let pastedData = e.clipboardData.getData('text')
    pastedData = normalizeDigits(pastedData)
    if (!/^\d*$/.test(pastedData)) return

    handleChange(index, pastedData)
  }

  const colsMapper = {
    4: 'grid-cols-4',
    5: 'grid-cols-5',
    6: 'grid-cols-6',
  }

  useEffect(() => {
    if (onComplete) {
      const code = normalizeDigits(otp.join(''))
      onComplete(code)
    }
  }, [otp, onComplete])

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  const isEmail = mobileOrEmail.includes('@')

  return (
    <Col className='gap-8'>
      <Typography size='sm' weight='regular' className='text-center'>
        {tRich.rich('common_otp_description', {
          mobile: () => (
            <Button
              variant='ghost'
              size='xs'
              onClick={onEdit}
              className='px-xs'
              endIcon={
                <Icon name='PencilWithALine' color='fill-base-main' size='xs' />
              }
            >
              <bdi
                className={twMerge('mt-2xs', isEmail ? 'font-[AzarMehr]' : '')}
              >
                {isEmail ? mobileOrEmail : mobileOrEmail.replace('+98', '0')}
              </bdi>
            </Button>
          ),
        })}
      </Typography>
      <Row className={`grid ${colsMapper[OTP_LENGTH]} gap-2 ltr`}>
        {Array.from({ length: OTP_LENGTH }).map((_, index) => (
          <Input
            key={index}
            autoComplete={index === 0 ? 'one-time-code' : undefined}
            autoFocus={index === 0}
            ref={setRef(index)}
            value={otp?.[index] ?? ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            onPaste={(e) => handlePaste(e, index)}
            className='w-full text-center text-lg'
            inputSize='lg'
            inputMode='numeric'
          />
        ))}
      </Row>
      <Row className='justify-center'>
        <OtpResend
          onResend={handleResend}
          loading={resendLoading}
          error={resendError}
        />
      </Row>
    </Col>
  )
}
